import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/*
 * EVIDENCE-VIEWER PORT of kaneai's MobileSkinOverlay
 * (apps/kaneai-test-management-client/src/components/v4/shared/MobileSkinOverlay.tsx)
 * — values/logic copied, NOT imported. If the source changes, mirror here.
 * Skin source in the viewer is result.yaml environment.skin_url/skin_data
 * (kaneai reads test_config from the testdetail API — same shape).
 */
import React, { useEffect, useRef, useState } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Ratio of the skin's inner viewport hole to the full skin image, as
 * percentages. Callers cap the media box's max-width/max-height with these
 * so the absolutely-positioned skin overlay has room for its bezels
 * without being clipped by an ancestor's overflow.
 */
export var getMobileSkinInnerRatios = function getMobileSkinInnerRatios(skin) {
  var _data$left, _data$right, _data$top, _data$bottom;
  var data = skin === null || skin === void 0 ? void 0 : skin.skin_data;
  if (!(data !== null && data !== void 0 && data.width) || !(data !== null && data !== void 0 && data.height)) return null;
  var innerW = data.width - ((_data$left = data.left) !== null && _data$left !== void 0 ? _data$left : 0) - ((_data$right = data.right) !== null && _data$right !== void 0 ? _data$right : 0);
  var innerH = data.height - ((_data$top = data.top) !== null && _data$top !== void 0 ? _data$top : 0) - ((_data$bottom = data.bottom) !== null && _data$bottom !== void 0 ? _data$bottom : 0);
  if (innerW <= 0 || innerH <= 0) return null;
  return {
    widthPct: innerW / data.width * 100,
    heightPct: innerH / data.height * 100
  };
};
var SKIN_CDN = 'https://applive-devtool.lambdatest.com/device-assets/skins';

/**
 * Per-slug skin geometry, MEASURED from each PNG's alpha channel (the CDN
 * serves only the image; it sends no CORS headers, so the hole can't be
 * measured in-browser — each new device_slug needs a one-time offline
 * measurement added here until backend authors skin_data in the pack).
 */
var SKIN_GEOMETRY = {
  'galaxy-note10-5g': {
    top: 69,
    right: 14,
    bottom: 30,
    left: 14,
    width: 645,
    height: 1350
  },
  'iphone-15-pro-max': {
    top: 33,
    right: 20,
    bottom: 33,
    left: 21,
    width: 470,
    height: 967
  }
};

/**
 * Interim default when a mobile pack authors no device_slug (packs sealed
 * before 2026-08-14) — authored environment skin_url/skin_data always win.
 */
export var DEFAULT_MOBILE_SKIN = {
  skin_url: "".concat(SKIN_CDN, "/skin_real-galaxy-note10-5g.png"),
  skin_data: SKIN_GEOMETRY['galaxy-note10-5g']
};

/**
 * The skin for a run's environment: authored skin keys win; then the
 * device_slug picks the CDN asset (only when its geometry is in
 * SKIN_GEOMETRY — a wrong-device frame is worse than the default);
 * slug-less mobile packs keep the interim default.
 */
export function skinForEnv(env) {
  var _env$platform;
  if (env.skinUrl && env.skinData) {
    return {
      skin_url: env.skinUrl,
      skin_data: env.skinData
    };
  }
  if (((_env$platform = env.platform) === null || _env$platform === void 0 ? void 0 : _env$platform.toLowerCase()) !== 'mobile') return null;
  var slug = env.deviceSlug;
  if (slug && SKIN_GEOMETRY[slug]) {
    return {
      skin_url: "".concat(SKIN_CDN, "/skin_real-").concat(slug, ".png"),
      skin_data: SKIN_GEOMETRY[slug]
    };
  }
  return DEFAULT_MOBILE_SKIN;
}
var getIntrinsicDimensions = function getIntrinsicDimensions(element) {
  if (element instanceof HTMLVideoElement) {
    return {
      width: element.videoWidth || 0,
      height: element.videoHeight || 0
    };
  }
  return {
    width: element.naturalWidth || 0,
    height: element.naturalHeight || 0
  };
};
var getReadyEventName = function getReadyEventName(element) {
  return element instanceof HTMLVideoElement ? 'loadedmetadata' : 'load';
};

/**
 * Renders the device frame ("skin") image as an absolute overlay precisely
 * aligned with the rendered media. Purely decorative (pointer-events none,
 * aria-hidden); a ResizeObserver keeps it aligned through loads/reflows.
 */
export function MobileSkinOverlay(_ref) {
  var skin = _ref.skin,
    getMediaElement = _ref.getMediaElement,
    wrapperRef = _ref.wrapperRef;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    overlayStyle = _useState2[0],
    setOverlayStyle = _useState2[1];
  var rafRef = useRef(null);
  useEffect(function () {
    var skinData = skin === null || skin === void 0 ? void 0 : skin.skin_data;
    var skinUrl = skin === null || skin === void 0 ? void 0 : skin.skin_url;
    if (!skinUrl || !(skinData !== null && skinData !== void 0 && skinData.width) || !(skinData !== null && skinData !== void 0 && skinData.height)) return undefined;
    var cancelled = false;
    var resizeObserver = null;
    var mediaElement = null;
    var readyEventName = null;
    var retryTimerId = null;
    var computeStyle = function computeStyle() {
      if (cancelled) return;
      try {
        var _skinData$top, _skinData$right, _skinData$bottom, _skinData$left, _skinData$width, _skinData$height;
        var wrapper = wrapperRef.current;
        if (!wrapper || !mediaElement) return;
        var boxRect = mediaElement.getBoundingClientRect();
        var wrapperRect = wrapper.getBoundingClientRect();
        var boxWidth = boxRect.width;
        var boxHeight = boxRect.height;
        if (!boxWidth || !boxHeight) return;

        // object-fit: contain media letterboxes inside its CSS box — the
        // skin's hole must align with the painted content, not the box.
        var _getIntrinsicDimensio = getIntrinsicDimensions(mediaElement),
          intrinsicW = _getIntrinsicDimensio.width,
          intrinsicH = _getIntrinsicDimensio.height;
        var contentWidth = boxWidth;
        var contentHeight = boxHeight;
        var contentLeft = boxRect.left - wrapperRect.left;
        var contentTop = boxRect.top - wrapperRect.top;
        if (intrinsicW > 0 && intrinsicH > 0) {
          var boxRatio = boxWidth / boxHeight;
          var contentRatio = intrinsicW / intrinsicH;
          if (contentRatio > boxRatio) {
            contentHeight = boxWidth / contentRatio;
            contentTop += (boxHeight - contentHeight) / 2;
          } else {
            contentWidth = boxHeight * contentRatio;
            contentLeft += (boxWidth - contentWidth) / 2;
          }
        }
        var top = (_skinData$top = skinData.top) !== null && _skinData$top !== void 0 ? _skinData$top : 0;
        var right = (_skinData$right = skinData.right) !== null && _skinData$right !== void 0 ? _skinData$right : 0;
        var bottom = (_skinData$bottom = skinData.bottom) !== null && _skinData$bottom !== void 0 ? _skinData$bottom : 0;
        var left = (_skinData$left = skinData.left) !== null && _skinData$left !== void 0 ? _skinData$left : 0;
        var skinNativeWidth = (_skinData$width = skinData.width) !== null && _skinData$width !== void 0 ? _skinData$width : 0;
        var skinNativeHeight = (_skinData$height = skinData.height) !== null && _skinData$height !== void 0 ? _skinData$height : 0;
        var innerWidth = skinNativeWidth - left - right;
        var innerHeight = skinNativeHeight - top - bottom;
        if (innerWidth <= 0 || innerHeight <= 0) return;

        // The hole's aspect matches the device's by design — pick the
        // smaller scale so the hole stays inside the visible content.
        var scaleX = contentWidth / innerWidth;
        var scaleY = contentHeight / innerHeight;
        var scale = Math.min(scaleX, scaleY);
        if (!Number.isFinite(scale) || scale <= 0) return;
        var overlayWidth = skinNativeWidth * scale;
        var overlayHeight = skinNativeHeight * scale;
        var contentCenterX = contentLeft + contentWidth / 2;
        var contentCenterY = contentTop + contentHeight / 2;
        setOverlayStyle({
          position: 'absolute',
          top: contentCenterY - overlayHeight / 2,
          left: contentCenterX - overlayWidth / 2,
          width: overlayWidth,
          height: overlayHeight,
          pointerEvents: 'none',
          zIndex: 2
        });
      } catch (_unused) {
        // Decorative only — never let geometry failures break the media.
      }
    };
    var scheduleCompute = function scheduleCompute() {
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(function () {
        rafRef.current = null;
        computeStyle();
      });
    };
    var attachObservers = function attachObservers(element) {
      if (typeof ResizeObserver === 'undefined') return;
      try {
        resizeObserver = new ResizeObserver(scheduleCompute);
        resizeObserver.observe(element);
        if (wrapperRef.current) resizeObserver.observe(wrapperRef.current);
      } catch (_unused2) {
        resizeObserver = null;
      }
    };
    var _tryAttach = function tryAttach() {
      if (cancelled) return;
      try {
        var element = getMediaElement();
        if (element instanceof HTMLVideoElement || element instanceof HTMLImageElement) {
          mediaElement = element;
          readyEventName = getReadyEventName(element);
          attachObservers(element);
          element.addEventListener(readyEventName, scheduleCompute);
          scheduleCompute();
          return;
        }
      } catch (_unused3) {
        // ignore and retry
      }
      // Refs can briefly be null during re-renders — retry until ready.
      retryTimerId = window.setTimeout(_tryAttach, 200);
    };
    _tryAttach();
    window.addEventListener('resize', scheduleCompute);
    return function () {
      cancelled = true;
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (retryTimerId != null) window.clearTimeout(retryTimerId);
      if (resizeObserver) {
        try {
          resizeObserver.disconnect();
        } catch (_unused4) {
          // ignore
        }
      }
      if (mediaElement && readyEventName) {
        try {
          mediaElement.removeEventListener(readyEventName, scheduleCompute);
        } catch (_unused5) {
          // ignore
        }
      }
      window.removeEventListener('resize', scheduleCompute);
    };
  }, [skin, getMediaElement, wrapperRef]);
  if (!overlayStyle || !(skin !== null && skin !== void 0 && skin.skin_url)) return null;
  return /*#__PURE__*/_jsx("img", {
    src: skin.skin_url,
    alt: "",
    "aria-hidden": "true",
    draggable: false,
    style: overlayStyle
  });
}